import { createVNode } from 'vue'
import { Modal, message } from 'ant-design-vue'
import { ExclamationCircleOutlined } from '@ant-design/icons-vue'
import CryptoJS from 'crypto-js'
import serviceSttus from '@/service/status'
import axios, { AxiosError } from 'axios'
import { RouteLocationNormalized, useRouter } from 'vue-router'
import { useSettingStore, useAuthStore, useTabStore } from '@/store'
import HTTP_CODE_STATUS from './httpCode'
import { IUserInfoProp, IFormStateProps, IResProps } from '@/service/model/index'
const { VITE_APP_AES_KEY } = import.meta.env

//跳转至登录页
export function reLogin() {
  location.pathname !== '/login' && location.replace(`/login`)
  clearData()
}
export const clearData = () => {
  const [authStore, tabStore, settingStore] = [useAuthStore(), useTabStore(), useSettingStore()]
  const storeList = [tabStore, authStore]
  const clearFieldArray = ['auth', 'tabs']
  /** 循环清空pinia中对应store */
  storeList.forEach((item) => {
    item['$reset']()
  })
  /** 循环清除localStorage存储的本地缓存key */
  clearFieldArray.forEach((_) => {
    localStorage.removeItem(_)
  })
  // 清空寄件入库扫描
  settingStore.setScanDataList([])
}
//服务器正常响应数据接口错误处理
export function requestFail(data: any) {
  /** 弹窗只允许出现一个 */
  message.config({
    maxCount: 1
  })
  message.error(data?.msg ?? lang(data?.code) ?? lang('未知错误'))
  if (HTTP_CODE_STATUS.INVALID.includes(Number(data?.code)))
    setTimeout(() => {
      reLogin()
    }, 1000)
}
//服务器错误处理
export function requestError(err: AxiosError) {
  console.log(err)
  const { response } = err
  let msg = lang('网络错误！请检查网络！')
  if (err.message.indexOf('timeout') !== -1) {
    msg = lang('请求超时')
  }

  if (response) {
    msg = response.status in serviceSttus ? serviceSttus[response.status] : lang('未知错误')
  }
  message.error(msg)
  if (HTTP_CODE_STATUS.INVALID.includes(Number(response?.status))) setTimeout(() => reLogin(), 1500)
}

export function hasProp(obj: any, prop: string): boolean {
  return Object.prototype.hasOwnProperty.call(obj, prop)
}

export const objectToString = Object.prototype.toString
export const toTypeString = (value: any) => objectToString.call(value)
export const hasOwnProperty = Object.prototype.hasOwnProperty
export const hasOwn = (val: any, key: any) => hasOwnProperty.call(val, key)
export const isNumber = (val: any) => toTypeString(val) === '[object Number]'
export const isMap = (val: any) => toTypeString(val) === '[object Map]'
export const isEmptyArray = (array: any[]) => Array.isArray(array) && array.length === 0
export const isSet = (val: any) => toTypeString(val) === '[object Set]'
export const isDate = (val: any) => toTypeString(val) === '[object Date]'
export const isFunction = (val: any) => typeof val === 'function'
export const isString = (val: any) => typeof val === 'string'
export const isSymbol = (val: any) => typeof val === 'symbol'
export const isObject = (val: any) => val !== null && typeof val === 'object'
export const isPromise = (val: any) => {
  return isObject(val) && isFunction(val.then) && isFunction(val.catch)
}

/**
 * 枚举值转select options
 * @param enumVal 枚举数据
 * @param hasAll 是否返回全部选项
 * @returns options
 */
export const getOptionsByEntry = (enumObj: any, hasAll = false): any => {
  const options = Object.entries(enumObj).map((_: any) => ({ label: lang(_[1]), value: _[0] }))
  if (hasAll) options.unshift({ label: lang('全部'), value: '' })
  return options
}

/**
 * 根据枚举对象值获取键
 * @param enumObj 枚举对象
 * @param enumValue 枚举值
 * @returns string
 */
export function getEnumKeyByEnumValue<TEnumKey extends string, TEnumVal extends string | number>(
  enumObj: { [key in TEnumKey]: TEnumVal },
  enumValue: TEnumVal
): string {
  return (Object.keys(enumObj) as TEnumKey[]).find((x) => enumObj[x] === enumValue) ?? ''
}

/**
 * 显示银行卡卡号
 * @param num 银行卡号码
 * @param type 显示后四位或全部 1 不加密 2 加密
 */

export const getCardNum = (num: string | undefined, type: 1 | 2 = 1) => {
  return type === 2 ? '****' + (num || '').slice(-4) : num
}
/**
 * 手机号脱敏第5-8位包含60
 * @param str 手机号
 * @returns string
 */
export const encryptPhone = (str: string) => {
  const strArr = str.split('')
  strArr.splice(4, 4, '****')
  return strArr.join('')
}

export function isSuccess(res: any) {
  const SpecialCodes = [
    HTTP_CODE_STATUS.SUCCESS
  ]
  return res?.code && SpecialCodes.includes(res.code)
}
interface IOptions {
  text: string
  value: any
}
export function array2Map(array: IOptions[]) {
  const map: Record<string, string> = {}
  if (Array.isArray(array)) {
    array.forEach((item) => (map[item.value] = item.text))
  }
  return map
}
/** 获取32位随机UUID */
export function getUUID(len = 32, _radix?: number) {
  const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.split('')
  const uuid = []
  const radix = _radix || chars.length
  if (len) {
    for (let i = 0; i < len; i++) {
      uuid[i] = chars[0 | (Math.random() * radix)]
    }
  } else {
    let r
    uuid[8] = uuid[13] = uuid[18] = uuid[23] = '-'
    uuid[14] = '4'
    for (let i = 0; i < 36; i++) {
      if (!uuid[i]) {
        r = 0 | (Math.random() * 16)
        uuid[i] = chars[i === 19 ? (r & 0x3) | 0x8 : r]
      }
    }
  }
  return uuid.join('')
}

/**
 * 动态化meta属性
 * */
export function restructureMeta(to: RouteLocationNormalized) {
  const { meta } = to
  return meta
}

/**
 * 省市区扁平数组转树
 * @param items 省市区扁平数组
 * @returns 省市区tree
 */
export function arrayToTree(items: Record<string, any>[]) {
  const result = []
  const itemMap = {} //
  for (const item of items) {
    const id = item.areaId
    const pid = item.parentId

    if (!itemMap[id]) {
      itemMap[id] = {
        children: []
      }
    }
    itemMap[id] = {
      ...item,
      children: itemMap[id]['children']
    }
    const treeItem = itemMap[id]
    if (pid == 0) {
      result.push(treeItem)
    } else {
      if (!itemMap[pid]) {
        itemMap[pid] = {
          children: []
        }
      }
      itemMap[pid].children.push(treeItem)
    }
  }
  return result
}

// 国际化全局翻译方法过滤器
export function lang(str: string) {
  const settingStore = useSettingStore()
  const label = settingStore.langJson?.[str]
  return label || str || ''
}

/** AES加密 */
export function aesEncrypt(content: string) {
  const key = CryptoJS.enc.Utf8.parse(VITE_APP_AES_KEY)
  const srcs = CryptoJS.enc.Utf8.parse(content)
  const encrypted = CryptoJS.AES.encrypt(srcs, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7
  })
  return encrypted.toString()
}

/**aes解密 */
export function aesDecrypt(content: string) {
  const key = CryptoJS.enc.Utf8.parse(VITE_APP_AES_KEY)
  const decrypted = CryptoJS.AES.decrypt(content, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7
  })
  return CryptoJS.enc.Utf8.stringify(decrypted)
}

/**排序的函数 */
export function objKeySort(data: object) {
  const obj = {}
  Object.keys(data)
    .sort()
    .forEach(function (item) {
      if (typeof data[item] !== 'object') {
        obj[item] = data[item]
      } else if (toString.call(data[item]) === '[object Array]') {
        obj[item] = []
        data[item].forEach((i: object, k: number) => {
          if (typeof i === 'object') {
            obj[item][k] = objKeySort(i)
          } else {
            obj[item][k] = i
          }
        })
      } else if (data[item] !== null && typeof data[item] === 'object') {
        obj[item] = objKeySort(data[item])
      }
    })
  return obj
}

/**删除数组属性为空 */
export function deleteNullKey(obj: object) {
  Object.keys(obj).forEach((item) => {
    if (obj[item] === null) {
      delete obj[item]
    } else if (toString.call(obj[item]) === '[object Array]') {
      obj[item].forEach((i: object, k: number) => {
        obj[item][k] = deleteNullKey(i)
      })
    } else if (obj[item] !== null && typeof obj[item] === 'object') {
      obj[item] = deleteNullKey(obj[item])
    }
  })
  return obj
}
export function makeMap(arr: Record<string, any>, value: string) {
  if (!value) return false
  return arr.find((item: Record<string, any>) => item.value === value).label
}
/** 过滤枚举字段 */
export function formatData(data: Record<string, any>, keyArr: string[]) {
  if (data === null) return false
  const _data = data
  Object.keys(data).forEach((item: string) => {
    if (keyArr.includes(item) && data[item] !== null && Object.prototype.hasOwnProperty.call(data[item], 'id')) {
      _data[item] = data[item].id
    } else if (toString.call(data[item]) === '[object Array]') {
      data[item].forEach((i: Record<string, any>, k: number) => {
        _data[item][k] = formatData(i, keyArr)
      })
    } else if (data[item] !== null && typeof data[item] === 'object') {
      _data[item] = formatData(data[item], keyArr)
    }
  })
  return _data
}

/** 校验一个值是否位null */
export function validatenull(val: any) {
  if (typeof val === 'boolean') {
    return false
  }
  if (typeof val === 'number') {
    return false
  }
  if (val instanceof Array) {
    if (val.length === 0) return true
  } else if (val instanceof Object) {
    if (JSON.stringify(val) === '{}') return true
  } else {
    if (val === 'null' || val == null || val === 'undefined' || val === undefined || val === '') {
      return true
    }
    return false
  }
  return false
}

/**修改刷新事件提示语 */
export const loadText = (e: any) => {
  // 不是所有浏览器都支持提示信息的修改
  const confirmationMessage =
    '您所查找的网页要输入已输入的信息，\n' + '返回此页面可能需要重复已进行的所有操作。是否要继续操作'
  e.returnValue = confirmationMessage
  return confirmationMessage
}

/** base64 加密*/
export const enBase64 = (str: string) => {
  const wordArray = CryptoJS.enc.Utf8.parse(str)
  return CryptoJS.enc.Base64.stringify(wordArray)
}

/** base64 解密*/
export const deBase64 = (str: string) => {
  return CryptoJS.enc.Base64.parse(str).toString(CryptoJS.enc.Utf8)
}

/**
 * 获取子节点的所有父级
 * @param nodeId 子节点
 * @param tree 扁平结构树
 * @returns parentIds
 */
export function findParents(nodeId: number, tree: any[]) {
  const node = tree.find((n) => n.id === nodeId)
  if (!node) return []

  const parentIds = [node.parentId]
  let parent = tree.find((n) => n.id === node.parentId)

  while (parent) {
    parentIds.unshift(parent.id)
    parent = tree.find((n) => n.id === parent.parentId)
  }
  return parentIds
}

/** 获取ant-image 容错图片 */
export function getErrorImage(url?: string) {
  return new URL(`../assets/${url || 'fallback'}.png`, import.meta.url).href
}

/** 开启表格拖动列并对该列宽度赋值 */
export function changeResizeColumn(w: any, col: { width: any }) {
  col.width = w
}

/** 获取表格动态计算高度 */
export function getTableHeight(value: number) {
  return `calc(100vh - ${value}px)`
}

// 千分位分隔符
export function numberToCurrencyNo(value: number) {
  if (!value) return 0
  // 获取整数部分
  const intPart = Math.trunc(value)
  // 整数部分处理，增加,
  const intPartFormat = intPart.toString().replace(/(\d)(?=(?:\d{3})+$)/g, '$1,')
  // 预定义小数部分
  let floatPart = ''
  // 将数值截取为小数部分和整数部分
  const valueArray = value.toString().split('.')
  if (valueArray.length === 2) {
    // 有小数部分
    floatPart = valueArray[1].toString() // 取得小数部分
    return intPartFormat + '.' + floatPart
  }
  return intPartFormat + floatPart
}

/**
 * 用户信息格式化（将接口返回的json格式化成订单录入页面所需的结构）
 * @param sourceObject 订单录入页面数据源对象
 * @param resInfo 详情接口返回的数据
 * @returns
 */
export function formatterCustomerInfo(sourceObject: IFormStateProps, resInfo: IResProps) {
  const enumFileds = ['payType', 'isQuote']
  const volumeFileds = ['goodsLength', 'goodsWidth', 'goodsHeight', 'volumeWeight']
  const postalCodeFileds = ['senderPostcode', 'recipientPostalCode']
  Object.assign(sourceObject, resInfo)
  /** 枚举字段格式化 */
  enumFileds.forEach((element) => {
    sourceObject[element] = resInfo[element]?.id
  })

  /** 体积字段格式化 */
  volumeFileds.forEach((element) => {
    sourceObject.volume[element] = resInfo[element] || 0
  })

  /** 邮编字段格式化 */
  postalCodeFileds.forEach((element) => {
    sourceObject[element] = resInfo[element]
  })

  sourceObject.userInfoState.forEach((_: IUserInfoProp) => {
    Object.assign(_.info, resInfo)
    /** 所在地区格式化处理  寄件人所在地区两级，收件人所在地区四级 */
    Object.assign(
      _.info[`${_.type}Address`],
      _.type === 'sender'
        ? [resInfo[`${_.type}ProvinceCode`], resInfo[`${_.type}CityCode`]]
        : [
            resInfo[`${_.type}ProvinceCode`],
            resInfo[`${_.type}CityCode`],
            resInfo[`${_.type}AreaCode`],
            resInfo[`${_.type}TownCode`]
          ]
    )
  })
  return sourceObject
}

/**
 * 获取当前设备时区
 * @returns
 */
export function getTimezoneOffset() {
  let timezoneOffset = new Intl.DateTimeFormat().resolvedOptions().timeZone
  return timezoneOffset
}

/**
 * 校验网点信息
 * @param res 接口响应数据
 * @param cb 回调函数
 * @returns
 */
export function checkNetWorkInfo(res: { code: number }, router: any, cb: Function) {
  const authStore = useAuthStore()
  if (authStore?.userInfo?.staffType?.id === 'EMPLOYER') {
    if (res.code === 70015) {
      Modal.confirm({
        title: lang('温馨提示'),
        icon: createVNode(ExclamationCircleOutlined),
        content: lang('请前往绑定寄件网点'),
        onOk: () => {
          router.push('/sendSetting')
        },
        onCancel: () => {
          console.log('Cancel')
        }
      })
      return false
    }
  } else {
    if (res.code === 70015) {
      Modal.warning({
        title: lang('温馨提示'),
        content: lang('请联系站长绑定寄件网点')
      })
      return false
    }
  }
  if (!checkFeeError(res)) return false
  cb()
}

/**
 * @description: 运费异常 判断
 * @param {object} res
 * @return {*}
 */
export const checkFeeError = (res: { code: number }):boolean => {
  if (res.code === 40036) {
    Modal.error({
      title: lang('运费异常'),
      content: lang('跨城件不允许入库')
    })
    return false
  }
  return true
}

/**
 * 千位分隔符
 * @param num  需要格式化的参数
 * @returns
 */
export function toThousands(num: string) {
  let result = '',
    counter = 0
  let dot = String(num).indexOf('.')
  if (dot != -1) {
    // alert("有小数点");
    // 获取小数点后面的数字(indexOf和substring都不支持数字，所以要先转字符串才可以用)
    let dotCnt = String(num).substring(dot + 1, num.length)

    // 获取小数点前面的数字
    num = String(num).split('.')[0]
    num = (num || 0).toString()
    for (var i = num.length - 1; i >= 0; i--) {
      counter++
      result = num.charAt(i) + result
      if (!(counter % 3) && i != 0) {
        result = '.' + result
      }
    }
    result = result + '.' + dotCnt
    return result
  } else {
    // alert("没有小数点");
    return (num || 0).toString().replace(/(\d)(?=(?:\d{3})+$)/g, '$1.')
  }
}

